import { Either, Schema } from "effect";

export const DEFAULT_DEV_AUTH_ORIGINS = Object.freeze([
	"http://localhost:3000",
	"http://localhost:3001",
	"http://localhost:5178",
	"http://127.0.0.1:3000",
	"http://127.0.0.1:3001",
	"http://127.0.0.1:5178",
	"http://localhost:3210",
	"http://localhost:3211",
] as const);

export function toOrigin(value: string | null | undefined): string | null {
	if (!value) {
		return null;
	}

	try {
		return new URL(value).origin;
	} catch {
		return null;
	}
}

export function parseOriginCsv(value: string | undefined): string[] {
	if (!value) {
		return [];
	}

	return value
		.split(",")
		.map((part) => toOrigin(part.trim()))
		.filter((origin): origin is string => Boolean(origin));
}

export function isHttpsOrigin(origin: string): boolean {
	return origin.startsWith("https://");
}

export function isHttpsUrl(value: string | null | undefined): boolean {
	if (!value) {
		return false;
	}

	try {
		return new URL(value).protocol === "https:";
	} catch {
		return false;
	}
}

export function isLoopbackOrigin(value: string | null | undefined): boolean {
	if (!value) {
		return false;
	}

	try {
		const { hostname } = new URL(value);
		return (
			hostname === "localhost" ||
			hostname === "::1" ||
			hostname === "[::1]" ||
			/^127(?:\.\d{1,3}){3}$/.test(hostname)
		);
	} catch {
		return false;
	}
}

export function isLocalNetworkOrigin(origin: string): boolean {
	try {
		const { hostname } = new URL(origin);
		return (
			hostname === "localhost" ||
			hostname === "::1" ||
			hostname === "[::1]" ||
			/^127(?:\.\d{1,3}){3}$/.test(hostname) ||
			/^10(?:\.\d{1,3}){3}$/.test(hostname) ||
			/^192\.168(?:\.\d{1,3}){2}$/.test(hostname) ||
			/^172\.(1[6-9]|2\d|3[01])(?:\.\d{1,3}){2}$/.test(hostname)
		);
	} catch {
		return false;
	}
}

export function normalizePrivateOriginToLocalhost(origin: string | null): string | null {
	if (!origin) {
		return null;
	}

	try {
		const url = new URL(origin);
		const hostname = url.hostname;
		const isPrivate =
			/^10(?:\.\d{1,3}){3}$/.test(hostname) ||
			/^192\.168(?:\.\d{1,3}){2}$/.test(hostname) ||
			/^172\.(1[6-9]|2\d|3[01])(?:\.\d{1,3}){2}$/.test(hostname);
		if (isPrivate) {
			url.hostname = "localhost";
			return url.origin;
		}
	} catch {
		return origin;
	}

	return origin;
}

/**
 * Returns the other half of a public apex ↔ www pair (https only).
 * Skips subdomains like internal.example.com (no bogus www.internal host).
 */
export function httpsWwwApexAlternate(origin: string): string | null {
	try {
		const url = new URL(origin);
		if (url.protocol !== "https:") {
			return null;
		}

		const parts = url.hostname.split(".");
		if (parts.length === 2) {
			return `https://www.${url.hostname}`;
		}
		if (parts.length === 3 && parts[0] === "www") {
			return `https://${parts.slice(1).join(".")}`;
		}

		return null;
	} catch {
		return null;
	}
}

export function expandWwwApexPairs(origins: Set<string>): void {
	for (const origin of [...origins]) {
		const alternate = httpsWwwApexAlternate(origin);
		if (alternate) {
			origins.add(alternate);
		}
	}
}

export const OriginSchema = Schema.String.pipe(
	Schema.filter((value) => toOrigin(value) === value, {
		description: "URL origin",
	}),
);

export const HttpsOriginSchema = OriginSchema.pipe(
	Schema.filter(isHttpsOrigin, {
		description: "https URL origin",
	}),
);

export function decodeOrigin(value: unknown): Either.Either<string, unknown> {
	const decoded = Schema.decodeUnknownEither(OriginSchema)(value);
	return Either.isRight(decoded) ? Either.right(decoded.right) : Either.left(decoded.left);
}
